import React, { useMemo } from 'react';
import { diffWords, Change } from 'diff';

/**
 * Two-column word-level text diff. Old (with removed spans) on the left, new
 * (with added spans) on the right. On narrow widths the columns stack.
 *
 * We diff the RAW text (markdown included) so any change to custom markdown is
 * visible — the surgical-edit guarantee is only trustworthy if the reviewer can
 * see exactly what changed.
 */
export interface DiffTextProps {
  oldText?: string | null;
  newText?: string | null;
  /** Optional column labels (e.g. "Current" / "Proposed"). */
  oldLabel?: string;
  newLabel?: string;
  className?: string;
}

const renderSide = (parts: Change[], side: 'old' | 'new') => {
  return parts.map((part, i) => {
    if (side === 'old' && part.added) return null;
    if (side === 'new' && part.removed) return null;

    if (part.added) {
      return (
        <span key={i} className="rounded-sm bg-emerald-100 text-emerald-800 px-0.5">
          {part.value}
        </span>
      );
    }
    if (part.removed) {
      return (
        <span key={i} className="rounded-sm bg-rose-100 text-rose-700 line-through decoration-rose-400 px-0.5">
          {part.value}
        </span>
      );
    }
    return <span key={i}>{part.value}</span>;
  });
};

const Panel: React.FC<{ label?: string; tone: 'old' | 'new'; children: React.ReactNode }> = ({
  label,
  tone,
  children,
}) => (
  <div className="flex-1 min-w-0">
    {label && (
      <div
        className={`mb-1 text-[11px] font-medium uppercase tracking-wide ${
          tone === 'old' ? 'text-rose-500' : 'text-emerald-600'
        }`}
      >
        {label}
      </div>
    )}
    <div className="rounded border border-gray-200 bg-white px-3 py-2 text-[13px] leading-relaxed text-gray-800 whitespace-pre-wrap break-words max-h-72 overflow-y-auto">
      {children}
    </div>
  </div>
);

const DiffText: React.FC<DiffTextProps> = ({
  oldText,
  newText,
  oldLabel = 'Current',
  newLabel = 'Proposed',
  className,
}) => {
  const parts = useMemo(() => diffWords(oldText ?? '', newText ?? ''), [oldText, newText]);

  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${className ?? ''}`}>
      <Panel label={oldLabel} tone="old">
        {(oldText ?? '').length ? renderSide(parts, 'old') : <span className="text-gray-400 italic">empty</span>}
      </Panel>
      <Panel label={newLabel} tone="new">
        {(newText ?? '').length ? renderSide(parts, 'new') : <span className="text-gray-400 italic">empty</span>}
      </Panel>
    </div>
  );
};

export default DiffText;
